import type { List, RecordOf } from 'immutable';
import { List as ImmutableList } from 'immutable';

import { type ACCOUNTS_REDUCER_KEY, accountsSelectors, accountsUtils } from 'app-components/flux/accounts';
import { type CATEGORIES_REDUCER_KEY, categoriesSelectors } from 'app-components/flux/categories';
import { mkChartOfAccount } from 'app-components/flux/chart-of-accounts/chartOfAccountsTypes';
import { getIdentityKeys, type IdentityLike } from 'app-components/flux/core/resourceStoreTypes';
import type { Receipt, ReceiptExtraction } from 'app-components/flux/receipts/receiptsTypes';
import { ensureJsArray, getLength } from 'app-components/utils/core/immutableHelpers';
import type RootState from 'app-components/utils/redux-store/rootState';

import { chartOfAccountsTypes, chartOfAccountsUtils } from '../chart-of-accounts';
import {
  hasExplicitSplitExclusionOverride,
  isSplitTxn,
  isTransferOrCreditCardPaymentTxn,
} from './transactionsPredicates';
import {
  mkSplit,
  mkSplitItem,
  mkSplitItems,
  type SplitItemProps,
  type SplitProps,
  type TransactionItem,
  type TransactionItemCombinedProps,
  type TransactionProps,
  type TransactionRecord,
} from './transactionsTypes';

/**
 * Transaction split helpers.
 *
 * This module is the single source of truth for split-related business logic shared by web and mobile.
 * Keep everything platform-agnostic (no web-only or mobile-only imports).
 *
 * ## Data shapes
 * - `SplitProps.items` can be:
 *   - `SplitItemProps[]` (plain JS array), or
 *   - `List<RecordOf<SplitItemProps>>` (ImmutableJS list of records), or
 *   - `null | undefined`
 * - Many reducers/store paths use Immutable `RecordOf<SplitProps>` / `RecordOf<SplitItemProps>`.
 *
 * ## Ordering
 * The order of `split.items` is the single source of truth for split ordering.
 * - Use `splitItemsToArray(...)` when you need canonical array order.
 * - Use `splitItemsToArrayOverflowLast(...)` when UI/persistence logic needs overflow rows forced to the end.
 *
 * ## Overflow split line (Series splits)
 * Series splits have a special overflow placeholder line:
 * - Identified by `isOverflow: true`
 * - Must be kept at amount `0` and rendered last in UI.
 *
 * ## Section guide
 * 1. Types & constants
 * 2. Internal (non-exported) helpers
 * 3. Conversion & query utilities
 * 4. Summation / balance checking
 * 6. freeToSPendSelectors helpers
 * 7. SplitDialog state helpers
 * 8. Scheduled-transaction derivation
 */

// ================================================================
// 1. Types & constants
// ================================================================

/** Plain-JS version of `SplitProps` with items always as a plain array. */
export type PlainSplitProps = Omit<SplitProps, 'items'> & { items?: SplitItemProps[] | null };

export type ReceiptSplitCoa = NonNullable<SplitItemProps['coa']>;

export type ReceiptSplitItem<TCoa extends ReceiptSplitCoa = ReceiptSplitCoa> = Pick<
  SplitItemProps,
  'amount' | 'businessId' | 'name'
> & {
  coa: TCoa;
};

export type ReceiptSplitSeed<TCoa extends ReceiptSplitCoa = ReceiptSplitCoa> = {
  amount: number;
  split: Omit<SplitProps, 'items'> & { items: ReceiptSplitItem<TCoa>[] };
};

export type ReceiptSplitOptions<TCoa extends ReceiptSplitCoa = ReceiptSplitCoa> = {
  uncategorizedCoa: TCoa;
};

/** Mode for the V2 SplitDialog (shared across web and mobile). */
export type SplitDialogModeV2 = 'transaction' | 'series' | 'reminder';

export type SplitQbpLineKind =
  | 'business-income'
  | 'business-expense'
  | 'business-account'
  | 'client-invoices'
  | 'other';

export type SplitQbpLinePolicy = {
  kind: SplitQbpLineKind;
  showClientProjectByDefault: boolean;
  showExclusionsByDefault: boolean;
  allowsClientProject: boolean;
  allowsExclusions: boolean;
  allowsBusinessAttachments: boolean;
  allowsPaymentOrnaments: boolean;
};

export type SplitQbpLineDefaultExclusions = Pick<SplitItemProps, 'isExcludedFromF2S' | 'isExcludedFromReports'>;

export type SplitPaymentIntentLike = {
  splitKey: string;
};

export type SplitItemExclusionPhase = 'seed' | 'categoryChange';

export type SplitItemParentExclusions = {
  isExcludedFromF2S?: boolean | null;
  isExcludedFromReports?: boolean | null;
};

export type SplitItemResolvedExclusions = {
  isExcludedFromF2S?: boolean;
  isExcludedFromReports?: boolean;
};

export type SplitWithLinkedItem = Pick<SplitItemProps, 'id' | 'clientId' | 'linkedItem'>;

export type LinkedInvoiceIndex = {
  /** invoice.id or invoice.clientId → invoice.id */
  byInvoiceIdentity: ReadonlyMap<string, string>;
  /** invoice split item's id or clientId → invoice.id */
  byInvoiceSplitItemIdentity: ReadonlyMap<string, string>;
  /** invoice split item's linkedItem (id/clientId/childId/childClientId) → invoice.id */
  byInvoiceSplitLinkedItemIdentity: ReadonlyMap<string, string>;
};

export type TransactionByIdLookup = {
  get?: (key: string) => TransactionRecord | undefined | null;
  forEach?: (fn: (value: TransactionRecord, key: string) => void) => void;
} | null;

type SplitItemExclusionContextItem = Pick<SplitItemProps, 'amount' | 'coa' | 'transfer'>;
type SplitLineQbpPolicyState = Pick<RootState, typeof ACCOUNTS_REDUCER_KEY | typeof CATEGORIES_REDUCER_KEY>;
type AccountTypeLookup =
  | {
      get?: (id: string) => { type?: string | null } | undefined | null;
    }
  | null
  | undefined;

const UNCATEGORIZED_COA = mkChartOfAccount({ id: '0', type: chartOfAccountsTypes.CoaTypeEnum.UNCATEGORIZED });
const LEGACY_NA_COA_TYPE = 'N/A';

export type SplitFieldDefaults = {
  tags: boolean;
  note: boolean;
  exclusions: boolean;
};

export const SPLIT_FIELD_DEFAULTS: SplitFieldDefaults = {
  tags: false,
  note: false,
  exclusions: false,
};

export const equalSplitFieldDefaults = (a: SplitFieldDefaults, b: SplitFieldDefaults): boolean =>
  a.tags === b.tags && a.note === b.note && a.exclusions === b.exclusions;

/** `allowedByPolicy` wins — it can hide a field even when `alwaysShow` is on. */
export const shouldShowSplitField = ({
  alwaysShow,
  hasValue,
  allowedByPolicy,
}: {
  alwaysShow: boolean;
  hasValue: boolean;
  allowedByPolicy: boolean;
}): boolean => allowedByPolicy && (alwaysShow || hasValue);

// ================================================================
// 2. Internal (non-exported) helpers
// ================================================================

function assert(condition: unknown, message: string): asserts condition {
  if (!condition) throw new Error(message);
}

const isAccountsReceivableSplitAccountType = (accountType?: string | null): boolean =>
  accountType === 'ACCOUNTS_RECEIVABLE';

export const isARSplitCoaFromLookup = (
  coa: Pick<NonNullable<SplitItemProps['coa']>, 'id' | 'type'> | null | undefined,
  accountsById: AccountTypeLookup,
): boolean => {
  if (coa?.type !== 'ACCOUNT' || !coa.id) {
    return false;
  }

  return isAccountsReceivableSplitAccountType(accountsById?.get?.(coa.id)?.type);
};

export const isARSplitCoa = (
  coa: Pick<NonNullable<SplitItemProps['coa']>, 'id' | 'type'> | null | undefined,
  state: SplitLineQbpPolicyState,
): boolean =>
  isARSplitCoaFromLookup(coa, {
    get: (id) => accountsSelectors.getAccountById(state, id) ?? undefined,
  });
export const isBusinessAccountSplitCoa = (
  coa: Pick<NonNullable<SplitItemProps['coa']>, 'id' | 'type'> | null | undefined,
  state: SplitLineQbpPolicyState,
): boolean => {
  if (coa?.type !== 'ACCOUNT' || !coa.id) {
    return false;
  }

  const account = accountsSelectors.getAccountById(state, coa.id);
  return Boolean(account && accountsUtils.isQBPAccount(account));
};

export const getSplitQbpLineKind = ({
  splitItem,
  state,
}: {
  splitItem: Pick<SplitItemProps, 'coa'>;
  state: SplitLineQbpPolicyState;
}): SplitQbpLineKind => {
  if (isARSplitCoa(splitItem.coa, state)) {
    return 'client-invoices';
  }

  if (isBusinessAccountSplitCoa(splitItem.coa, state)) {
    return 'business-account';
  }

  if (splitItem.coa?.type !== 'CATEGORY' || !splitItem.coa.id) {
    return 'other';
  }

  const category = categoriesSelectors.getCategoryById(state, splitItem.coa.id);
  const categoryType = category?.type ?? category?.categoryType;
  if (category?.usageType !== 'BUSINESS') {
    return 'other';
  }

  if (categoryType === 'INCOME' || categoriesSelectors.isIncomeCat(state, splitItem.coa.id)) {
    return 'business-income';
  }

  if (categoryType === 'EXPENSE' || categoriesSelectors.isExpenseCat(state, splitItem.coa.id)) {
    return 'business-expense';
  }

  return 'other';
};

export const doesSplitModeAllowPaymentOrnaments = (mode: SplitDialogModeV2): boolean => mode === 'transaction';

export const isQbpBusinessClassifiedLine = (kind: SplitQbpLineKind): boolean =>
  kind === 'business-income' || kind === 'business-expense' || kind === 'client-invoices';

export const getSplitQbpLineDefaultExclusions = (kind: SplitQbpLineKind): SplitQbpLineDefaultExclusions | undefined => {
  switch (kind) {
    case 'business-income':
    case 'business-expense':
      return {
        isExcludedFromF2S: true,
        isExcludedFromReports: false,
      };
    case 'client-invoices':
    case 'business-account':
      return {
        isExcludedFromF2S: true,
        isExcludedFromReports: true,
      };
    case 'other':
    default:
      return undefined;
  }
};
export const getSplitQbpLinePolicy = ({
  splitItem,
  mode,
  state,
  businessEnabled = true,
}: {
  splitItem: Pick<SplitItemProps, 'coa'>;
  mode: SplitDialogModeV2;
  state: SplitLineQbpPolicyState;
  businessEnabled?: boolean;
}): SplitQbpLinePolicy => {
  const kind = getSplitQbpLineKind({ splitItem, state });

  if (!businessEnabled) {
    // Exclusions ("Exclude from Reports" / "Exclude from Spending Plan") are core
    // features that apply to every user, so keep them enabled even when business
    // (QBP) features are disabled. Everything business-related stays off.
    return {
      kind,
      showClientProjectByDefault: false,
      showExclusionsByDefault: false,
      allowsClientProject: false,
      allowsExclusions: true,
      allowsBusinessAttachments: false,
      allowsPaymentOrnaments: false,
    };
  }

  const isBusinessClassified = isQbpBusinessClassifiedLine(kind);

  return {
    kind,
    showClientProjectByDefault: isBusinessClassified,
    showExclusionsByDefault: isBusinessClassified,
    allowsClientProject: isBusinessClassified,
    allowsExclusions: kind !== 'business-account',
    allowsBusinessAttachments: isBusinessClassified,
    allowsPaymentOrnaments: doesSplitModeAllowPaymentOrnaments(mode) && kind !== 'business-account',
  };
};

/**
 * Invoice-payment affordances only apply to positive A/R lines.
 * Negative A/R rows behave like credits or reversals, not assignable payments.
 */
export const isInvoicePaymentSplitItem = (
  item: Pick<SplitItemProps, 'amount' | 'coa'> | null | undefined,
  accountsById: AccountTypeLookup,
): boolean => {
  const amount = item?.amount ?? 0;

  return amount > 0 && isARSplitCoaFromLookup(item?.coa, accountsById);
};

export const countInvoicePaymentSplits = (
  txn: Pick<TransactionProps, 'split'> | null | undefined,
  accountsById: AccountTypeLookup,
): number =>
  ensureJsArray(txn?.split?.items).reduce(
    (sum, item) => sum + (isInvoicePaymentSplitItem(item, accountsById) ? 1 : 0),
    0,
  );

export const findFirstInvoicePaymentSplitIndex = (
  txn: Pick<TransactionProps, 'split'> | null | undefined,
  accountsById: AccountTypeLookup,
): number => ensureJsArray(txn?.split?.items).findIndex((item) => isInvoicePaymentSplitItem(item, accountsById));

/**
 * After save, split rows may move from a draft `clientId` key to a persisted `id`.
 * Match queued payment intents against either identity and only reopen rows that
 * still qualify as invoice-payment splits.
 */
export const findPendingSplitPaymentIndex = ({
  accountsById,
  pendingSplitPaymentIntent,
  txn,
}: {
  accountsById: AccountTypeLookup;
  pendingSplitPaymentIntent?: SplitPaymentIntentLike | null;
  txn: Pick<TransactionProps, 'split'> | null | undefined;
}): number => {
  if (!pendingSplitPaymentIntent) {
    return -1;
  }

  return ensureJsArray(txn?.split?.items).findIndex(
    (item) =>
      (item?.id === pendingSplitPaymentIntent.splitKey || item?.clientId === pendingSplitPaymentIntent.splitKey) &&
      isInvoicePaymentSplitItem(item, accountsById),
  );
};
/**
 * Returns split items as a JS array while preserving Immutable Record instances (no `toObject()`).
 * Used by internal helpers that re-wrap items via `mkSplitItems` or need record methods.
 */
const splitItemsToRecordArray = (items: SplitProps['items']): SplitItemProps[] => {
  if (!items) return [];
  return Array.isArray(items) ? items : (items as List<RecordOf<SplitItemProps>>).toArray();
};

/** Converts split items into an Immutable `List<RecordOf<SplitItemProps>>` (always fresh records). */
const splitItemsToRecordList = (split: RecordOf<SplitProps>): List<RecordOf<SplitItemProps>> =>
  ImmutableList(mkSplitItems(splitItemsToRecordArray(split.items)));

const collectStableStrings = (...values: Array<string | null | undefined>): string[] =>
  Array.from(new Set(values.filter((value): value is string => Boolean(value)).map(String)));

/** Identifies the legacy scheduled-series marker that is not a valid split-row COA. */
export const isLegacyNACoa = (coa: { type?: string | null } | null | undefined): boolean =>
  coa?.type === LEGACY_NA_COA_TYPE;

/** Chooses the COA for a repaired overflow row using the first valid non-overflow split row. */
const getSeriesOverflowFallbackCoa = (items: SplitItemProps[]): TransactionProps['coa'] => {
  const firstValidNonOverflowCoa =
    items.find((item) => !isOverflowSplitItem(item) && item.coa && !isLegacyNACoa(item.coa))?.coa ?? null;

  if (firstValidNonOverflowCoa && chartOfAccountsUtils.isTransferCOA(firstValidNonOverflowCoa)) {
    return UNCATEGORIZED_COA;
  }

  return firstValidNonOverflowCoa ?? UNCATEGORIZED_COA;
};

/** Repairs only legacy `N/A` COAs on series split rows before they are edited or persisted. */
const normalizeLegacyNACoasForSeriesSplitItems = (items: SplitItemProps[]): SplitItemProps[] => {
  const overflowFallbackCoa = getSeriesOverflowFallbackCoa(items);

  return items.map((item) => {
    if (!isLegacyNACoa(item.coa)) {
      return item;
    }

    return {
      ...item,
      coa: isOverflowSplitItem(item) ? overflowFallbackCoa : UNCATEGORIZED_COA,
    };
  });
};

/** Immutable split-state variant of series split row COA repair. */
const normalizeLegacyNACoasForSeriesSplitRecord = (split: RecordOf<SplitProps>): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);
  const overflowFallbackCoa = getSeriesOverflowFallbackCoa(splitItemsToArray(items));
  const normalizedItems = items.map((item) => {
    if (!isLegacyNACoa(item.coa)) {
      return item;
    }

    return item.set('coa', isOverflowSplitItem(item) ? overflowFallbackCoa : UNCATEGORIZED_COA);
  });

  return split.set('items', normalizedItems) as RecordOf<SplitProps>;
};

const isInvoiceTypeTransaction = (
  transaction: TransactionProps | TransactionRecord | null | undefined,
): transaction is TransactionProps | TransactionRecord => transaction?.type === 'INVOICE';

// ================================================================
// 3. Conversion & query utilities
// ================================================================

/** Normalizes a split line name for display and persistence across web and mobile. */
export const formatSplitItemName = (name: string | null | undefined): string | undefined => {
  const formattedName = name?.replace(/\s*[\r\n]+\s*/g, ' ').trim();
  return formattedName || undefined;
};

const toReceiptTransactionAmount = (value: number | undefined): number | undefined => {
  if (value === undefined || !Number.isFinite(value) || value === 0) {
    return undefined;
  }

  return -value;
};

const formatReceiptLineItemName = (description: string, quantity?: number): string | undefined => {
  const name = formatSplitItemName(description);
  return name && quantity !== undefined && Number.isFinite(quantity) && quantity > 1
    ? `${name} (Qty ${quantity})`
    : name;
};

const buildReceiptAmountRow = <TCoa extends ReceiptSplitCoa>(
  description: string,
  value: number | undefined,
  uncategorizedCoa: TCoa,
  quantity?: number,
): ReceiptSplitItem<TCoa> | undefined => {
  const amount = toReceiptTransactionAmount(value);
  if (amount === undefined) {
    return undefined;
  }

  return {
    amount,
    coa: uncategorizedCoa,
    name: formatReceiptLineItemName(description, quantity),
  };
};

/** Converts usable receipt extraction rows into transaction-signed split items. */
export function buildReceiptSplitItems<TCoa extends ReceiptSplitCoa>(
  extraction: ReceiptExtraction,
  options: ReceiptSplitOptions<TCoa>,
): ReceiptSplitItem<TCoa>[] | undefined;
export function buildReceiptSplitItems(extraction: ReceiptExtraction): ReceiptSplitItem[] | undefined;
export function buildReceiptSplitItems(
  extraction: ReceiptExtraction,
  { uncategorizedCoa }: ReceiptSplitOptions = { uncategorizedCoa: { id: '0', type: 'UNCATEGORIZED' } },
): ReceiptSplitItem[] | undefined {
  const lineItemRows =
    extraction.lineItems
      ?.map((item) =>
        item.description
          ? buildReceiptAmountRow(item.description, item.amount, uncategorizedCoa, item.quantity)
          : undefined,
      )
      .filter((item): item is ReceiptSplitItem => Boolean(item)) ?? [];
  const baseRows =
    lineItemRows.length > 0
      ? lineItemRows
      : [buildReceiptAmountRow('Subtotal', extraction.subtotal, uncategorizedCoa)].filter(
          (item): item is ReceiptSplitItem => Boolean(item),
        );

  if (baseRows.length === 0) {
    return undefined;
  }

  const additiveRows = [
    buildReceiptAmountRow('Tax', extraction.tax, uncategorizedCoa),
    buildReceiptAmountRow('Tip', extraction.tip, uncategorizedCoa),
  ].filter((item): item is ReceiptSplitItem => Boolean(item));
  const splitItems = [...baseRows, ...additiveRows];

  return splitItems.length >= 2 ? splitItems : undefined;
}

/** Builds the transaction amount and split payload used to initialize receipt-derived splits. */
export function buildReceiptSplitSeed<TCoa extends ReceiptSplitCoa>(
  receipt: Receipt,
  options: ReceiptSplitOptions<TCoa>,
): ReceiptSplitSeed<TCoa> | undefined;
export function buildReceiptSplitSeed(receipt: Receipt): ReceiptSplitSeed | undefined;
export function buildReceiptSplitSeed(receipt: Receipt, options?: ReceiptSplitOptions): ReceiptSplitSeed | undefined {
  const total = receipt.extraction?.total;
  const items = receipt.extraction
    ? options
      ? buildReceiptSplitItems(receipt.extraction, options)
      : buildReceiptSplitItems(receipt.extraction)
    : undefined;
  if (total === undefined || !items) {
    return undefined;
  }

  return { amount: -total, split: { items } };
}

/**
 * Intended to be used to hide rows in the Spending Plan UI.
 *
 * Rules:
 * - Always hide 0-amount account-transfer rows (both standalone transfer txns and transfer split lines).
 * @param txnItem - The transaction item to check.
 * @returns `true` if the spending plan row should be hidden, `false` otherwise.
 */
export const shouldHideSpendingPlanRowForTxnItem = (txnItem: TransactionItem): boolean => {
  const { item } = txnItem;
  const amount = Number(item.amount ?? 0);
  if (item.coa?.type === 'ACCOUNT' && amount === 0) return true;
  return false;
};

/**
 * Returns `true` if a split item is the special "Overflow" placeholder line (Series splits).
 */
export const isOverflowSplitItem = (item: SplitItemProps | TransactionItemCombinedProps): boolean =>
  item.isOverflow === true;

export const hasLinkedItemReference = (linkedItem: SplitItemProps['linkedItem'] | null | undefined): boolean =>
  Boolean(linkedItem?.id || linkedItem?.clientId || linkedItem?.childId || linkedItem?.childClientId);

export const isLinkedBillableSplitItem = <T extends SplitItemProps | TransactionItemCombinedProps>(
  item: T | null | undefined,
): item is T & { isBillable: true; linkedItem: NonNullable<SplitItemProps['linkedItem']> } =>
  Boolean(item?.isBillable && hasLinkedItemReference(item?.linkedItem));

export const splitHasTransferReference = (split: Pick<SplitItemProps, 'transfer'> | null | undefined): boolean =>
  Boolean(split?.transfer?.id || split?.transfer?.clientId);

export const getSplitLinkedItem = (
  split: SplitWithLinkedItem | null | undefined,
  initialSplit?: SplitWithLinkedItem,
): SplitItemProps['linkedItem'] | undefined => split?.linkedItem ?? initialSplit?.linkedItem ?? undefined;

const sameLinkedItem = (
  a: SplitItemProps['linkedItem'] | null | undefined,
  b: SplitItemProps['linkedItem'] | null | undefined,
): boolean => {
  const aRef = a ?? null;
  const bRef = b ?? null;
  if (aRef === bRef) return true;
  if (!aRef || !bRef) return false;
  return (
    (aRef.id ?? null) === (bRef.id ?? null) &&
    (aRef.clientId ?? null) === (bRef.clientId ?? null) &&
    (aRef.childId ?? null) === (bRef.childId ?? null) &&
    (aRef.childClientId ?? null) === (bRef.childClientId ?? null)
  );
};

/**
 * Force each working split item's `linkedItem` to match the authoritative server copy.
 *
 * `linkedItem` is server-owned billable-expense linkage and is NOT user-editable in the transaction
 * details dialog. When the server rebases the transaction (e.g. it unlinked the expense after its
 * invoice/estimate billable lines were removed), the working draft must adopt the server's value —
 * otherwise a stale link in the draft is re-saved, re-attaching the expense to an invoice that no
 * longer bills it. Items are matched by stable identity (`id` / `clientId`); an item with no server
 * counterpart is left untouched, and every other (user-editable) field is preserved. Expects
 * plain-JS split props (items as a plain array of plain objects).
 */
export const reconcileSplitLinkedItems = <T extends PlainSplitProps | SplitProps | null | undefined>(
  workingSplit: T,
  serverSplit: PlainSplitProps | SplitProps | RecordOf<SplitProps> | null | undefined,
): T => {
  if (!workingSplit) return workingSplit;
  const workingItems = ensureJsArray(workingSplit.items as SplitItemProps[] | null | undefined);
  if (workingItems.length === 0) return workingSplit;

  const serverLinkedByKey = new Map<string, SplitItemProps['linkedItem'] | null>();
  ensureJsArray(serverSplit?.items as SplitItemProps[] | null | undefined).forEach((serverItem) => {
    const linkedItem = serverItem?.linkedItem ?? null;
    for (const key of getIdentityKeys(serverItem as IdentityLike)) {
      serverLinkedByKey.set(key, linkedItem);
    }
  });

  let changed = false;
  const reconciledItems = workingItems.map((item) => {
    let matched = false;
    let nextLinkedItem: SplitItemProps['linkedItem'] | null = null;
    for (const key of getIdentityKeys(item as IdentityLike)) {
      if (serverLinkedByKey.has(key)) {
        nextLinkedItem = serverLinkedByKey.get(key) ?? null;
        matched = true;
        break;
      }
    }
    if (!matched || sameLinkedItem(item.linkedItem, nextLinkedItem)) return item;
    changed = true;
    return { ...item, linkedItem: nextLinkedItem };
  });

  if (!changed) return workingSplit;
  return { ...workingSplit, items: reconciledItems } as T;
};

/**
 * Project a split's items down to just their user-editable content, dropping the server-owned
 * `linkedItem` (billable-expense invoice linkage). Use this when deciding whether the user has
 * unsaved *edits*: `linkedItem` is set/cleared by the server as expenses are billed/unbilled, never
 * by the user in the transaction dialog, so a `linkedItem`-only difference between the working split
 * and the base must NOT count as an unsaved change (it otherwise triggers a phantom close prompt).
 */
export const omitLinkedItemsFromSplit = (
  split: PlainSplitProps | SplitProps | RecordOf<SplitProps> | null | undefined,
): Array<Omit<SplitItemProps, 'linkedItem'>> | null => {
  if (!split) return null;
  return ensureJsArray(split.items as SplitItemProps[] | null | undefined).map((item) => {
    const { linkedItem: _linkedItem, ...rest } = item;
    return rest;
  });
};

/**
 * Strict "is this billable expense still billed?" check.
 *
 * A billable expense split carries a `linkedItem` whose `id` is the INVOICE/ESTIMATE *transaction*
 * id. {@link resolveLinkedInvoiceIdFromIndex} resolves that via `byInvoiceIdentity` whenever the
 * invoice/estimate transaction merely still exists — even after its billable line items (the ones
 * that referenced this expense) have been removed. That permissive resolution is wrong for deciding
 * whether the expense is still billed.
 *
 * The authoritative signal is the *reverse* link: a live invoice/estimate split item must still
 * point back at this expense split. We therefore consult ONLY `byInvoiceSplitLinkedItemIdentity`
 * (keyed by the invoice line's `linkedItem` identifiers), matched against this expense split's own
 * identity and its `linkedItem` child identifiers — never `byInvoiceIdentity`.
 *
 * NOTE: callers must only treat a `false` result as "unbilled" once invoices/estimates are loaded
 * (a not-yet-loaded store has an empty reverse map, which would false-negative).
 */
export const isBillableSplitItemStillBilled = (
  item: SplitWithLinkedItem | null | undefined,
  index: LinkedInvoiceIndex | null | undefined,
): boolean => {
  if (!index || !hasLinkedItemReference(item?.linkedItem)) return false;
  const reverseKeys = collectStableStrings(
    ...getIdentityKeys(item),
    item?.linkedItem?.childId,
    item?.linkedItem?.childClientId,
  );
  return reverseKeys.some((key) => index.byInvoiceSplitLinkedItemIdentity.has(key));
};

const isLinkedInvoiceKnownLoaded = (
  item: SplitWithLinkedItem | null | undefined,
  index: LinkedInvoiceIndex | null | undefined,
): boolean => {
  if (!index || !hasLinkedItemReference(item?.linkedItem)) return false;
  return getIdentityKeys(item?.linkedItem).some((key) => index.byInvoiceIdentity.has(key));
};

/**
 * Clear `linkedItem` from any split line whose loaded invoice/estimate no longer has a billable
 * line pointing back at it. Keeping the stale link lets a save re-attach the expense to an invoice
 * that no longer bills it.
 *
 * Pass `index` built from the CURRENT store, and only when invoices/estimates are loaded — see
 * {@link isBillableSplitItemStillBilled}. The transaction store may still be date-filtered, so a
 * missing invoice identity is not enough proof that a link is dead; only strip when the invoice is
 * present in the index but its reverse billable line is gone. Expects plain-JS split props; preserves
 * all other fields and returns the same reference when nothing changes.
 */
export const stripDeadBillableLinks = <T extends PlainSplitProps | SplitProps | null | undefined>(
  split: T,
  index: LinkedInvoiceIndex | null | undefined,
): T => {
  if (!split || !index) return split;
  const items = ensureJsArray(split.items as SplitItemProps[] | null | undefined);
  if (items.length === 0) return split;

  let changed = false;
  const next = items.map((item) => {
    if (!hasLinkedItemReference(item?.linkedItem) || isBillableSplitItemStillBilled(item, index)) return item;
    if (!isLinkedInvoiceKnownLoaded(item, index)) return item;
    changed = true;
    return { ...item, linkedItem: null };
  });

  if (!changed) return split;
  return { ...split, items: next } as T;
};

/** Call once per store snapshot; share the result across rows in a single render. */
export const buildLinkedInvoiceIndex = (transactionsById: TransactionByIdLookup | undefined): LinkedInvoiceIndex => {
  const byInvoiceIdentity = new Map<string, string>();
  const byInvoiceSplitItemIdentity = new Map<string, string>();
  const byInvoiceSplitLinkedItemIdentity = new Map<string, string>();

  if (transactionsById?.forEach) {
    transactionsById.forEach((candidate) => {
      if (!isInvoiceTypeTransaction(candidate) || !candidate.id) return;
      const invoiceId = candidate.id;

      for (const key of getIdentityKeys(candidate)) {
        byInvoiceIdentity.set(key, invoiceId);
      }

      ensureJsArray(candidate.split?.items).forEach((invoiceSplit) => {
        for (const key of getIdentityKeys(invoiceSplit)) {
          byInvoiceSplitItemIdentity.set(key, invoiceId);
        }

        const linkedItem = invoiceSplit.linkedItem;
        if (!linkedItem) return;

        for (const key of collectStableStrings(
          linkedItem.id,
          linkedItem.clientId,
          linkedItem.childId,
          linkedItem.childClientId,
        )) {
          byInvoiceSplitLinkedItemIdentity.set(key, invoiceId);
        }
      });
    });
  }

  return { byInvoiceIdentity, byInvoiceSplitItemIdentity, byInvoiceSplitLinkedItemIdentity };
};

/**
 * Fallback order:
 * 1. linkedItem's own id/clientId → invoice id
 * 2. linkedItem's childId/childClientId → invoice-split id/clientId
 * 3. linkedItem's childId/childClientId → invoice-split linkedItem (any of 4 fields)
 * 4. split (or initialSplit) id/clientId → invoice-split linkedItem (any of 4 fields)
 */
export const resolveLinkedInvoiceIdFromIndex = ({
  split,
  initialSplit,
  index,
}: {
  split: SplitWithLinkedItem | null | undefined;
  initialSplit?: SplitWithLinkedItem;
  index: LinkedInvoiceIndex | null | undefined;
}): string | undefined => {
  if (!index) return undefined;

  const linkedItem = getSplitLinkedItem(split, initialSplit);
  if (!hasLinkedItemReference(linkedItem)) return undefined;

  for (const key of getIdentityKeys(linkedItem)) {
    const invoiceId = index.byInvoiceIdentity.get(key);
    if (invoiceId) return invoiceId;
  }

  const childLinkedValues = collectStableStrings(linkedItem?.childId, linkedItem?.childClientId);
  for (const key of childLinkedValues) {
    const invoiceId = index.byInvoiceSplitItemIdentity.get(key);
    if (invoiceId) return invoiceId;
  }

  for (const key of childLinkedValues) {
    const invoiceId = index.byInvoiceSplitLinkedItemIdentity.get(key);
    if (invoiceId) return invoiceId;
  }

  for (const key of collectStableStrings(...getIdentityKeys(split), ...getIdentityKeys(initialSplit))) {
    const invoiceId = index.byInvoiceSplitLinkedItemIdentity.get(key);
    if (invoiceId) return invoiceId;
  }

  return undefined;
};

/** Returns `true` if the collection contains at least one non-overflow split item. */
export const hasRealSplitItems = (
  items: ImmutableList<RecordOf<SplitItemProps>> | SplitItemProps[] | null | undefined,
): boolean => splitItemsToArray(items).some((item) => !isOverflowSplitItem(item));

/**
 * Returns `true` when an overflow row carries a materialized amount (e.g. matched reminder delta)
 * and callers should surface that numeric value instead of the "Overflow" placeholder label.
 */
export const shouldDisplayOverflowAmount = (
  split: Partial<Pick<SplitItemProps, 'isOverflow' | 'amount'>> | null | undefined,
): boolean => Boolean(split?.isOverflow) && Number(split?.amount ?? 0) !== 0;

/** Identifies empty series-template overflow rows that should stay out of display lists. */
export const isZeroAmountOverflowSplit = (
  split: Partial<Pick<SplitItemProps, 'isOverflow' | 'amount'>> | null | undefined,
): boolean => split?.isOverflow === true && Number(split.amount ?? 0) === 0;

/**
 * Returns `true` when the overflow split line's amount should be editable on a matched
 * scheduled/series transaction.
 *
 * Rule: editable once the parent transaction is no longer `SCHEDULED_TRANSACTION_PENDING`
 * (i.e., the scheduled transaction has been accepted/matched into the register). While the
 * parent is still pending the overflow row tracks the series template placeholder and must
 * stay locked so it does not drift from the template.
 */
export const isOverflowAmountEditable = (txn: Pick<TransactionProps, 'source'> | null | undefined): boolean =>
  txn?.source !== 'SCHEDULED_TRANSACTION_PENDING';

/**
 * Resolves the split line on `txn` referenced by a generated-report cell item (`splitItemId`).
 */
export const findSplitItemForReportCell = (
  txn: TransactionRecord | null | undefined,
  cellItem: { splitItemId?: string },
): SplitItemProps | RecordOf<SplitItemProps> | undefined => {
  if (!txn || !cellItem.splitItemId) {
    return undefined;
  }
  return txn.split?.items?.find((splitItem) => splitItem?.id === cellItem.splitItemId);
};

/**
 * Normalizes a `SplitProps.items` payload into a plain JS array of `SplitItemProps`.
 *
 * - `null`/`undefined` => `[]`
 * - `SplitItemProps[]` => returned as-is
 * - `List<RecordOf<SplitItemProps>>` => converted via `toObject()` per item
 */
export const splitItemsToArray = (
  items: ImmutableList<RecordOf<SplitItemProps>> | SplitItemProps[] | null | undefined,
): SplitItemProps[] => {
  if (!items) return [];
  if (Array.isArray(items)) return items;
  return items.toArray().map((item) => item.toObject());
};

/**
 * Applies a transaction's business context to prebuilt split rows that do not already declare usage.
 * Explicit business and Personal sentinel values are preserved.
 */
export function applyDefaultBusinessIdToSplitItems<T extends Pick<SplitItemProps, 'businessId'>>(
  items: T[],
  businessId: string | null | undefined,
): T[];
export function applyDefaultBusinessIdToSplitItems(
  items: SplitProps['items'],
  businessId: string | null | undefined,
): SplitItemProps[];
export function applyDefaultBusinessIdToSplitItems(
  items: SplitProps['items'],
  businessId: string | null | undefined,
): SplitItemProps[] {
  const splitItems = splitItemsToArray(items);
  const defaultBusinessId = accountsUtils.normalizeBusinessIdForUsage(businessId);
  if (!defaultBusinessId) {
    return splitItems;
  }

  return splitItems.map((item) =>
    item.businessId == null
      ? {
          ...item,
          businessId: defaultBusinessId,
        }
      : item,
  );
}

const getTransferIdentity = (txn: Pick<TransactionProps, 'transfer'> | null | undefined): IdentityLike =>
  txn?.transfer ?? null;

export const getTxnByTransferLink = (
  transactionsById: TransactionByIdLookup,
  transfer: { id?: string | null; clientId?: string | null } | null,
): TransactionRecord | null => {
  if (!transactionsById?.get || !transfer) return null;

  if (transfer.id) {
    const txnById = transactionsById.get(String(transfer.id)) ?? null;
    if (txnById) return txnById;
  }

  if (!transfer.clientId || !transactionsById.forEach) return null;
  let found: TransactionRecord | null = null;
  transactionsById.forEach((candidate) => {
    if (found) return;
    if (candidate?.clientId && candidate.clientId === transfer.clientId) {
      found = candidate;
    }
  });
  return found;
};

export const splitItemHasTransferLinkToTxn = (args: {
  splitItem: Pick<SplitItemProps, 'transfer'> | null | undefined;
  txn: Pick<TransactionProps, 'id' | 'clientId'> | null | undefined;
}): boolean => {
  const { splitItem, txn } = args;
  const txnIdentityKeys = new Set(getIdentityKeys(txn));
  if (txnIdentityKeys.size === 0) return false;

  return getIdentityKeys(splitItem?.transfer).some((linkedTxnKey) => txnIdentityKeys.has(linkedTxnKey));
};

export const splitItemsContainTransferLinkToTxn = (args: {
  splitItems: ImmutableList<RecordOf<SplitItemProps>> | SplitItemProps[] | null | undefined;
  txn: Pick<TransactionProps, 'id' | 'clientId'> | null | undefined;
}): boolean => {
  const { splitItems, txn } = args;
  return splitItemsToArray(splitItems ?? []).some((splitItem) => splitItemHasTransferLinkToTxn({ splitItem, txn }));
};

/**
 * Fields the source split owns on a transfer counterpart. `state`, `isReviewed`, and metadata
 * fields like `memo`/`tags` are intentionally excluded so the counterpart can manage them locally.
 *
 * Keep in sync with `preserveSplitTransferGuardedFields` (this file),
 * `doesTxnMatchGuardedTransferFields`, and `getConnectedSplitTransferSourceComparison` (preProcess.ts).
 */
export const SPLIT_TRANSFER_GUARDED_FIELDS = [
  'amount',
  'postedOn',
  'coa',
  'businessId',
  'customerId',
  'projectId',
] as const;

export type SplitTransferGuardedField = (typeof SPLIT_TRANSFER_GUARDED_FIELDS)[number];

/**
 * Returns `true` if `txn` is the transfer-counterpart side of a split transfer.
 *
 * Pattern:
 * - `txn.transfer` points to a parent transfer transaction.
 * - The parent transaction has split items.
 * - One split item links back to `txn` via `splitItem.transfer.id/clientId`.
 */
export const isSplitTransferCounterpart = (args: {
  txn: Pick<TransactionProps, 'id' | 'clientId' | 'transfer'> | null | undefined;
  transactionsById: TransactionByIdLookup;
}): boolean => {
  const { txn, transactionsById } = args;

  const transfer = getTransferIdentity(txn);
  if (!transfer?.id && !transfer?.clientId) return false;

  const parentTxn = getTxnByTransferLink(transactionsById, transfer);
  if (!parentTxn || !isSplitTxn(parentTxn)) return false;

  return splitItemsContainTransferLinkToTxn({
    splitItems: parentTxn.split?.items ?? [],
    txn,
  });
};

/**
 * Series-only counterpart check. Requires `stModelId` before checking the transfer link.
 *
 * This is used by Spending Plan to suppress raw "other-side" transfer rows and rely on split-derived
 * rows instead. Prefer `isSplitTransferCounterpart` for general non-series editing guards.
 */
export const isOtherSideOfTransferTxnASplit = (args: {
  txn: Pick<TransactionProps, 'id' | 'clientId' | 'stModelId' | 'transfer'> | null | undefined;
  transactionsById: TransactionByIdLookup;
}): boolean => {
  if (!args.txn?.stModelId) return false;
  return isSplitTransferCounterpart(args);
};

export const isOtherSideOfSplitTransferTxn = isOtherSideOfTransferTxnASplit;

/**
 * Restores fields owned by the source split before persisting edits to the transfer counterpart.
 *
 * `accountId` and linkage fields are also restored defensively because moving or unlinking the
 * counterpart would break the back-link to the source split item.
 */
export const preserveSplitTransferGuardedFields = ({
  previous,
  next,
}: {
  previous: TransactionRecord;
  next: TransactionRecord;
}): TransactionRecord => {
  const guardedRestore: Pick<TransactionProps, SplitTransferGuardedField> = {
    amount: previous.amount,
    postedOn: previous.postedOn,
    coa: previous.coa,
    businessId: previous.businessId,
    customerId: previous.customerId,
    projectId: previous.projectId,
  };
  return next.merge({
    accountId: previous.accountId,
    stModelId: previous.stModelId,
    transfer: previous.transfer,
    ...guardedRestore,
  });
};

export type ScheduledModelTxnsByIdLookup = {
  get?: (key: string) => { transaction?: TransactionProps | TransactionRecord | null | undefined } | null | undefined;
} | null;

export const getPostedDateKey = (
  txn: { postedOn?: string | null; stDueOn?: string | null } | null | undefined,
): string | null => {
  const raw = txn?.postedOn ?? txn?.stDueOn ?? null;
  if (!raw) return null;
  // `postedOn` is typically `YYYY-MM-DD` but may also be an ISO timestamp; normalize to date portion.
  return raw.length >= 10 ? raw.slice(0, 10) : raw;
};

/**
 * Given a transaction that was generated from a scheduled transaction split line, locate the split item
 * on the corresponding scheduled transaction instance that produced it.
 *
 * Steps:
 * - Extract the transfer identity, if present, and use it to find the other-side txn.
 * - Determine the scheduled model id (`stModelId`) from the txn or its transfer counterparty.
 * - Look up the scheduled model.
 * - Find a scheduled instance transaction on the same posted date as `txn`.
 * - Return the split item whose `splitItem.transfer.id/clientId` matches the original txn id/clientId.
 */
export function getScheduledInstanceSplitItemForTxn(args: {
  txn: TransactionProps | TransactionRecord;
  transactionsById?: TransactionByIdLookup;
  scheduledModelTxnsById?: ScheduledModelTxnsByIdLookup;
}): SplitItemProps | null {
  const { txn, transactionsById, scheduledModelTxnsById } = args;

  if (!txn?.id && !txn?.clientId) return null;

  const postedKey = getPostedDateKey(txn);
  if (!postedKey) return null;

  const otherSide = getTxnByTransferLink(transactionsById ?? null, txn.transfer ?? null) ?? undefined;

  const modelId = (txn.stModelId ?? otherSide?.stModelId ?? null) as string | null;
  if (!modelId) return null;

  const model = scheduledModelTxnsById?.get ? scheduledModelTxnsById.get(modelId) : undefined;
  if (!model) return null;

  const tryFindInInstance = (
    instance: TransactionRecord | TransactionProps | null | undefined,
  ): SplitItemProps | null => {
    if (!instance) return null;
    if (getPostedDateKey(instance) !== postedKey) return null;
    const items = splitItemsToArray(instance.split?.items ?? null);
    if (!items.length) return null;
    const found = items.find((splitItem) => splitItemHasTransferLinkToTxn({ splitItem, txn }));
    return found ?? null;
  };

  // Fast path: if the transfer counterparty is the instance, search it first.
  const fromOtherSide = tryFindInInstance(otherSide ?? null);
  if (fromOtherSide) return fromOtherSide;

  // Slow path: scan known transactions for a matching instance on the same date for this model.
  if (transactionsById?.forEach) {
    let found: SplitItemProps | null = null;
    transactionsById.forEach((candidate) => {
      if (found) return;
      if ((candidate.stModelId ?? null) !== modelId) return;
      const res = tryFindInInstance(candidate);
      if (res) found = res;
    });
    if (found) return found;
  }

  // Templates often lack instance-level transfer ids, so this best-effort fallback is often a miss.
  const result = tryFindInInstance(model.transaction);
  return result ?? null;
}

export const isOtherSideOfTransferTxnASeriesSplit = ({
  txn,
  transactionsById,
  scheduledModelTxnsById,
}: {
  txn: TransactionProps | TransactionRecord;
  transactionsById: TransactionByIdLookup;
  scheduledModelTxnsById?: ScheduledModelTxnsByIdLookup;
}): boolean =>
  isOtherSideOfTransferTxnASplit({ txn, transactionsById }) ||
  Boolean(getScheduledInstanceSplitItemForTxn({ txn, transactionsById, scheduledModelTxnsById }));

export const isOtherSideOfSeriesSplitTransferTxn = isOtherSideOfTransferTxnASeriesSplit;

/**
 * Covers hydrated counterparts and scheduled-only counterparts whose parent is not in `transactionsById` yet.
 */
export const isAnySplitTransferCounterpart = (args: {
  txn: Pick<TransactionProps, 'id' | 'clientId' | 'stModelId' | 'transfer'> | (TransactionProps | TransactionRecord);
  transactionsById: TransactionByIdLookup;
  scheduledModelTxnsById?: ScheduledModelTxnsByIdLookup;
}): boolean =>
  isSplitTransferCounterpart({ txn: args.txn, transactionsById: args.transactionsById }) ||
  Boolean(
    getScheduledInstanceSplitItemForTxn({
      txn: args.txn as TransactionProps | TransactionRecord,
      transactionsById: args.transactionsById,
      scheduledModelTxnsById: args.scheduledModelTxnsById,
    }),
  );

/**
 * Returns split items in canonical array order while forcing overflow rows to the end.
 * Relative order is preserved within both the non-overflow and overflow partitions.
 */
export const splitItemsToArrayOverflowLast = (
  items: ImmutableList<RecordOf<SplitItemProps>> | SplitItemProps[],
): SplitItemProps[] => {
  const arr = splitItemsToArray(items);
  if (arr.length < 2) return arr;

  const nonOverflow = arr.filter((item) => !isOverflowSplitItem(item));
  const overflow = arr.filter((item) => isOverflowSplitItem(item));
  return nonOverflow.concat(overflow);
};

/** Canonicalizes series split item order and removes legacy `N/A` split-row COAs. */
export const normalizeSeriesSplitItemsForPersistence = (items: SplitItemProps[]): SplitItemProps[] =>
  normalizeLegacyNACoasForSeriesSplitItems(splitItemsToArrayOverflowLast(items));

/**
 * Converts a `SplitProps` (or Immutable `RecordOf<SplitProps>`) into a plain JS object with a plain `items` array.
 * Useful at UI boundaries that prefer plain objects over Immutable records.
 */
export const splitToPlain = (split: SplitProps | RecordOf<SplitProps> | null | undefined): PlainSplitProps | null => {
  if (!split) return null;
  const raw = 'toObject' in split && typeof split.toObject === 'function' ? split.toObject() : split;
  return {
    ...raw,
    items: splitItemsToArray(raw.items),
  };
};

/**
 * Fills nullish split-item exclusion flags with transfer / credit-card-payment defaults.
 * Explicit boolean values always win (including `false` on transfer-like lines).
 */
export const applyDefaultSplitItemExclusions = (item: SplitItemProps): SplitItemProps => {
  const isTransfer = isTransferOrCreditCardPaymentTxn(item) || Boolean(item.transfer?.id ?? item.transfer?.clientId);
  const businessLike = !!(item.businessId || item.projectId || item.customerId);
  return {
    ...item,
    isExcludedFromF2S: item.isExcludedFromF2S ?? (isTransfer && businessLike), // don't exclude non-business split transfers from F2S by default
    isExcludedFromReports: item.isExcludedFromReports ?? isTransfer,
  };
};

const isTransferOrCreditCardPaymentSplitLine = (splitItem: Pick<SplitItemProps, 'coa' | 'transfer'>): boolean =>
  Boolean(
    (splitItem.coa &&
      (splitItem.coa.type === 'ACCOUNT' || chartOfAccountsUtils.isTransferLikeBalanceAdjustmentCoa(splitItem.coa))) ||
      splitItem.transfer?.id ||
      splitItem.transfer?.clientId,
  );

const getTransferLikeSplitItemExclusionDefaults = (): SplitItemParentExclusions => ({
  isExcludedFromF2S: true,
  isExcludedFromReports: true,
});

const getBusinessCategorySplitItemExclusionDefaults = (kind: SplitQbpLineKind): SplitItemParentExclusions | null => {
  if (kind === 'business-income' || kind === 'business-expense') {
    return {
      isExcludedFromF2S: true,
      isExcludedFromReports: false,
    };
  }

  return null;
};

const getSeedSplitItemExclusionDefaults = ({
  splitItem,
  mode,
  state,
}: {
  splitItem: SplitItemExclusionContextItem;
  mode: SplitDialogModeV2;
  state: SplitLineQbpPolicyState;
}): SplitItemParentExclusions | null => {
  const kind = getSplitQbpLineKind({ splitItem, state });
  const isTransferLike =
    isTransferOrCreditCardPaymentSplitLine(splitItem) && kind !== 'client-invoices' && kind !== 'business-account';

  if (isTransferLike) {
    return getTransferLikeSplitItemExclusionDefaults();
  }

  if (mode === 'transaction' && kind === 'client-invoices') {
    return getTransferLikeSplitItemExclusionDefaults();
  }

  return getBusinessCategorySplitItemExclusionDefaults(kind);
};

const getCategoryChangeSplitItemExclusionDefaults = ({
  splitItem,
  mode,
  state,
}: {
  splitItem: SplitItemExclusionContextItem;
  mode: SplitDialogModeV2;
  state: SplitLineQbpPolicyState;
}): SplitItemParentExclusions | null => {
  const kind = getSplitQbpLineKind({ splitItem, state });
  const isTransferLike =
    isTransferOrCreditCardPaymentSplitLine(splitItem) && kind !== 'client-invoices' && kind !== 'business-account';

  if (isTransferLike) {
    return getTransferLikeSplitItemExclusionDefaults();
  }

  if (mode === 'transaction' && kind === 'client-invoices') {
    return getTransferLikeSplitItemExclusionDefaults();
  }

  const businessDefaults = getBusinessCategorySplitItemExclusionDefaults(kind);
  if (businessDefaults) {
    return businessDefaults;
  }

  if (kind === 'business-account') {
    return {
      isExcludedFromF2S: false,
      isExcludedFromReports: false,
    };
  }

  return null;
};

export const resolveSplitItemExclusions = ({
  splitItem,
  mode,
  state,
  phase,
  parentExclusions,
}: {
  splitItem: Pick<SplitItemProps, 'amount' | 'coa' | 'transfer' | 'isExcludedFromF2S' | 'isExcludedFromReports'>;
  mode: SplitDialogModeV2;
  state: SplitLineQbpPolicyState;
  phase: SplitItemExclusionPhase;
  parentExclusions?: SplitItemParentExclusions;
}): SplitItemResolvedExclusions => {
  if (phase === 'seed') {
    const defaults = getSeedSplitItemExclusionDefaults({ splitItem, mode, state });

    return {
      isExcludedFromF2S:
        splitItem.isExcludedFromF2S ?? defaults?.isExcludedFromF2S ?? parentExclusions?.isExcludedFromF2S ?? undefined,
      isExcludedFromReports:
        splitItem.isExcludedFromReports ??
        defaults?.isExcludedFromReports ??
        parentExclusions?.isExcludedFromReports ??
        undefined,
    };
  }

  const defaults = getCategoryChangeSplitItemExclusionDefaults({ splitItem, mode, state });
  const hasExplicitOverrides = hasExplicitSplitExclusionOverride(splitItem);

  if (defaults) {
    return {
      isExcludedFromF2S: defaults.isExcludedFromF2S ?? undefined,
      isExcludedFromReports: defaults.isExcludedFromReports ?? undefined,
    };
  }

  if (hasExplicitOverrides) {
    return {
      isExcludedFromF2S: splitItem.isExcludedFromF2S ?? undefined,
      isExcludedFromReports: splitItem.isExcludedFromReports ?? undefined,
    };
  }

  if (mode === 'series') {
    return {
      isExcludedFromF2S: parentExclusions?.isExcludedFromF2S ?? undefined,
      isExcludedFromReports: parentExclusions?.isExcludedFromReports ?? undefined,
    };
  }

  return {
    isExcludedFromF2S: false,
    isExcludedFromReports: false,
  };
};

/** Prepares receipt rows with the same usage and exclusion rules as the transaction split editor. */
export const prepareReceiptSplitItems = ({
  items,
  businessId,
  state,
  parentExclusions,
}: {
  items: ReceiptSplitItem[];
  businessId: string | null | undefined;
  state: SplitLineQbpPolicyState;
  parentExclusions: SplitItemParentExclusions;
}): Array<ReceiptSplitItem & SplitItemResolvedExclusions> =>
  applyDefaultBusinessIdToSplitItems(items, businessId).map((item) => ({
    ...item,
    ...resolveSplitItemExclusions({ splitItem: item, mode: 'transaction', state, phase: 'seed', parentExclusions }),
  }));

export const materializeSeriesSplitItemExclusions = ({
  splitItem,
  state,
  parentExclusions,
}: {
  splitItem: Pick<SplitItemProps, 'amount' | 'coa' | 'transfer' | 'isExcludedFromF2S' | 'isExcludedFromReports'>;
  state: SplitLineQbpPolicyState;
  parentExclusions?: SplitItemParentExclusions;
}): SplitItemResolvedExclusions =>
  resolveSplitItemExclusions({
    splitItem,
    mode: 'series',
    state,
    // Use seed priority so explicit split toggles win over inherited parent exclusions at submit time.
    phase: 'seed',
    parentExclusions,
  });

export type RegisterSplitItemLike = RecordOf<SplitItemProps> | SplitItemProps | null | undefined;

const splitItemLikeToPlain = (splitItem: RegisterSplitItemLike): SplitItemProps | null => {
  if (!splitItem) {
    return null;
  }
  if (typeof splitItem === 'object' && 'toObject' in splitItem && typeof splitItem.toObject === 'function') {
    return splitItem.toObject() as SplitItemProps;
  }
  return splitItem as SplitItemProps;
};

/**
 * Effective Reports / Spending Plan exclusion for one split line, after the same defaults as
 * {@link applyDefaultSplitItemExclusions} (transfer and credit-card payment lines).
 */
export function getEffectiveSplitItemExclusionFlags(splitItem: RegisterSplitItemLike): {
  reports: boolean;
  spendingPlan: boolean;
} {
  const plain = splitItemLikeToPlain(splitItem);
  if (!plain) {
    return { reports: false, spendingPlan: false };
  }
  const withDefaults = applyDefaultSplitItemExclusions(plain);
  return {
    reports: Boolean(withDefaults.isExcludedFromReports),
    spendingPlan: Boolean(withDefaults.isExcludedFromF2S),
  };
}

/**
 * Row model for the register “Exclusion” column: parent txn, aggregate split parent row, or split line.
 * When the txn has splits and any line is excluded, {@link exclusionIconsMuted} is true (per-line editing).
 */
export function getRegisterExclusionRowFields(
  transaction: TransactionRecord,
  splitItem: RegisterSplitItemLike,
): {
  exclusion: boolean;
  excludeFromReports: boolean;
  excludeFromSpendingPlan: boolean;
  exclusionIconsMuted: boolean;
} {
  const hasSplitTxn = isSplitTxn(transaction);

  let excludeFromReports: boolean;
  let excludeFromSpendingPlan: boolean;

  if (splitItem) {
    const line = getEffectiveSplitItemExclusionFlags(splitItem);
    excludeFromReports = line.reports;
    excludeFromSpendingPlan = line.spendingPlan;
  } else if (hasSplitTxn) {
    excludeFromReports = false;
    excludeFromSpendingPlan = false;
    const items = splitItemsToArray(transaction.split?.items ?? []);
    for (const it of items) {
      if (isOverflowSplitItem(it)) continue;
      const line = getEffectiveSplitItemExclusionFlags(it);
      if (line.reports) excludeFromReports = true;
      if (line.spendingPlan) excludeFromSpendingPlan = true;
    }
  } else {
    excludeFromReports = Boolean(transaction.isExcludedFromReports);
    excludeFromSpendingPlan = Boolean(transaction.isExcludedFromF2S);
  }

  const exclusion = excludeFromReports || excludeFromSpendingPlan;
  const exclusionIconsMuted = Boolean(hasSplitTxn && exclusion);

  return { exclusion, excludeFromReports, excludeFromSpendingPlan, exclusionIconsMuted };
}

export const normalizeSplits = (
  split: SplitProps | RecordOf<SplitProps> | null | undefined,
): PlainSplitProps | undefined => {
  if (!split) return undefined;
  const itemsArray = ensureJsArray(split.items);

  if (itemsArray == null || itemsArray.length === 0) {
    return { doPatch: split.doPatch, items: [] };
  }

  return {
    doPatch: split.doPatch,
    items: (itemsArray ?? []).map((item) => applyDefaultSplitItemExclusions(item)),
  };
};

/** Applies the normal split-item persistence defaults plus series-only COA repair. */
export const normalizeSeriesSplitForPersistence = (
  split: SplitProps | RecordOf<SplitProps> | null | undefined,
): PlainSplitProps | undefined => {
  const normalized = normalizeSplits(split);
  if (!normalized) {
    return undefined;
  }

  return {
    ...normalized,
    items: normalizeSeriesSplitItemsForPersistence(normalized.items ?? []),
  };
};

// ================================================================
// 4. Summation / balance checking
// ================================================================

/**
 * Sums all split item amounts and rounds to 2 decimals.
 * Works with either array or Immutable List split items.
 */
export function sumSplitItems(split?: SplitProps | null) {
  let sum = 0;
  if (split && split.items) {
    split.items.forEach((item) => {
      sum += Number(item.amount);
    });
  }
  return Number(sum.toFixed(2));
}

/** Sums split item amounts excluding the overflow placeholder item(s). */
export const sumNonOverflowSplitItems = (split: SplitProps): number => {
  const items = splitItemsToRecordArray(split?.items);
  const sum = items.reduce((acc, item) => (isOverflowSplitItem(item) ? acc : acc + Number(item.amount ?? 0)), 0);
  return Number(sum.toFixed(2));
};

/**
 * Returns `true` when a split transaction's items do not sum to the transaction amount.
 * Non-split transactions always return `false`.
 */
export function isUnbalancedSplitTxn(txn: TransactionProps) {
  if (isSplitTxn(txn)) {
    return Number(txn.amount).toFixed(2) !== sumSplitItems(txn.split).toFixed(2);
  }
  return false;
}

/** Returns `txn.amount - sum(split.items)` rounded to 2 decimals. */
export const splitTxnRemainder = (txn: TransactionProps) =>
  Number((Number(txn.amount) - sumSplitItems(txn.split)).toFixed(2));

/**
 * Gets the left-to-split number by subtracting the sum of split items from the transaction amount.
 * - If `splits.items` exists but is empty, returns `initialAmount`.
 * - If `splits.items` is missing, returns `0` (caller typically treats this as "no split editing context").
 */
export const createLeftToSplit = (splits: SplitProps, initialAmount: number): number => {
  if (splits.items) {
    if (splitItemsToArray(splits.items).length === 0) {
      return initialAmount;
    }
    return initialAmount - sumSplitItems(splits);
  }
  return 0;
};

// ================================================================
// 5. Immutable split mutation helpers
// ================================================================

/**
 * Ensures overflow split items are at the end of the split list.
 * If there are no overflow items, returns the original split unchanged.
 */
export const ensureOverflowSplitIsLast = (split: RecordOf<SplitProps>): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);
  if (items.size === 0) {
    return split;
  }

  const partitioned = items.reduce(
    (acc, item) => {
      if (isOverflowSplitItem(item)) {
        acc.overflow.push(item);
      } else {
        acc.nonOverflow.push(item);
      }
      return acc;
    },
    {
      nonOverflow: [] as Array<RecordOf<SplitItemProps>>,
      overflow: [] as Array<RecordOf<SplitItemProps>>,
    },
  );

  if (partitioned.overflow.length === 0) {
    return split;
  }

  return split.set(
    'items',
    ImmutableList<RecordOf<SplitItemProps>>([...partitioned.nonOverflow, ...partitioned.overflow]),
  );
};

/**
 * Adds a remainder amount back into an existing split by:
 * - Preferentially updating an existing UNCATEGORIZED row (id=0), otherwise
 * - Creating a new UNCATEGORIZED row.
 *
 * Expects an Immutable `TransactionRecord` with Immutable split items.
 */
export function addRemainderToSplit(txn: TransactionRecord, remainder: number) {
  if (!txn.split || !txn.split.items) {
    // biome-ignore lint/suspicious/noConsole: flags a split helper call on a transaction without split items
    console.error('Txn has no split or no split items');
    return txn;
  }

  const itemIndex = txn.split.items.findIndex((item) =>
    Boolean(item.coa && item.coa.type === 'UNCATEGORIZED' && Number(item.coa.id) === 0),
  );

  if (itemIndex !== -1) {
    assert(!Array.isArray(txn.split.items), 'Split Items not immutable - Please tell Chris what you just did');
    const items = txn.split.items as List<RecordOf<SplitItemProps>>;
    // Non-null assertion because we are already checking the index from findIndex
    let item = items.get(itemIndex)!;
    item = item.set('amount', Number(item.amount) + remainder);
    const newItems = items.set(itemIndex, item);
    return txn.setIn(['split', 'items'], newItems);
  }
  // need to create the uncategorized row
  const newItem = mkSplitItem({
    amount: remainder,
    coa: mkChartOfAccount({ id: '0', type: 'UNCATEGORIZED' }),
  });
  return txn.setIn(['split', 'items'], txn.split.items.push(newItem));
}

/**
 * Consolidates multiple UNCATEGORIZED split rows into a single row whose amount is the sum of them.
 * If everything is uncategorized (only one row remains), reverts the txn back to a non-split
 * by setting `split=null` and `coa=UNCATEGORIZED`.
 *
 * Expects Immutable split items (`List<RecordOf<SplitItemProps>>`).
 */
export function consolidateUncatSplitItems(txn: TransactionRecord) {
  let updatedTxn = txn;
  if (!txn.split || !txn.split.items) {
    return updatedTxn;
  }
  if (isSplitTxn(txn)) {
    let total = 0;
    const consolidatedItems = (txn.split.items as List<RecordOf<SplitItemProps>>)
      .filter((item: SplitItemProps) => {
        // total uncategorized amount while removing them
        if (!item.coa || item.coa.type === 'UNCATEGORIZED') {
          total += Number(item.amount);
          return false;
        }
        return true;
      })
      .push(mkSplitItem({ coa: { id: '0', type: 'UNCATEGORIZED' }, amount: total })); // add total back

    // if everything was uncategorized so only one splitItem left, revert to normal coa
    if (consolidatedItems?.size === 1) {
      updatedTxn = updatedTxn.merge({ split: null, coa: { id: '0', type: 'UNCATEGORIZED' } });
    } else {
      updatedTxn = updatedTxn.setIn(['split', 'items'], consolidatedItems);
    }
  }
  return updatedTxn;
}

// ================================================================
// 6. freeToSPendSelectors helpers
// ================================================================
// These helpers are used by the freeToSPendSelectors specifically for handling splits

export const getOrderedSplitItemsForF2S = (
  modelSplitItems: SplitItemProps[],
  instanceSplitItems: SplitItemProps[],
): SplitItemProps[] => {
  const orderedModelSplitItems = splitItemsToArrayOverflowLast(modelSplitItems);
  const orderedInstanceSplitItems = splitItemsToArrayOverflowLast(instanceSplitItems);

  if (orderedModelSplitItems.length < 2) return orderedInstanceSplitItems;

  const out = orderedModelSplitItems.map((modelItem, index) => orderedInstanceSplitItems[index] ?? modelItem);
  if (orderedInstanceSplitItems.length > orderedModelSplitItems.length) {
    out.push(...orderedInstanceSplitItems.slice(orderedModelSplitItems.length));
  }
  return out;
};

/**
 * Gets the transfer split item for the F2S UI.
 * @param splitItem - The split item to get the transfer split item for.
 * @param otherSideForSplit - The other side of the split for the transfer.
 * @returns The transfer split item.
 */
export const getTransferSplitItemForF2S = (
  splitItem: SplitItemProps,
  otherSideForSplit: TransactionRecord | undefined,
): SplitItemProps | null => {
  const isSplitTransfer = splitItem.coa?.type === chartOfAccountsTypes.CoaTypeEnum.ACCOUNT;
  const splitTransferId = splitItem.transfer?.id ?? null;
  const splitAmount = Number(splitItem.amount ?? 0);
  const derivedTransferAmount =
    isSplitTransfer && splitTransferId && splitAmount === 0 && otherSideForSplit
      ? Number(otherSideForSplit.amount ?? 0) * -1
      : null;

  const effectiveSplitItemObj: SplitItemProps =
    derivedTransferAmount != null && derivedTransferAmount !== 0
      ? ({ ...splitItem, amount: derivedTransferAmount } as SplitItemProps)
      : splitItem;

  if (!isSplitTransfer) return effectiveSplitItemObj;

  return {
    ...effectiveSplitItemObj,
    isExcludedFromF2S: effectiveSplitItemObj.isExcludedFromF2S ?? true,
    isExcludedFromReports: effectiveSplitItemObj.isExcludedFromReports ?? true,
  };
};

// ================================================================
// 7. SplitDialog state helpers
// ================================================================

/**
 * Creates the init state for a new split by adding 2 lines:
 * - Line 1 inherits txn COA + tags + business/project/customer/billable context.
 * - Line 2 is blank (coa=null) to prompt the user for another split line.
 */
export const createInitState = (txn: TransactionProps): RecordOf<SplitProps> => {
  const splitItems: SplitItemProps[] = [];
  splitItems.push({
    coa: txn.coa || null,
    tags: txn.tags || undefined,
    businessId: txn.businessId || undefined,
    projectId: txn.projectId || undefined,
    customerId: txn.customerId || undefined,
    isBillable: txn.isBillable ?? undefined,
    amount: 0,
  });
  splitItems.push({
    coa: null,
    businessId: txn.businessId || undefined,
    amount: 0,
  });
  return mkSplit({ items: splitItems });
};

/**
 * Creates the init state for a series split assignment:
 * - 2 non-overflow lines (default to txn COA), plus
 * - 1 overflow placeholder line at the end.
 */
export const createSeriesInitState = (txn: TransactionProps): RecordOf<SplitProps> => {
  const splitItems: SplitItemProps[] = [];
  const defaultCoa = isLegacyNACoa(txn.coa) ? UNCATEGORIZED_COA : (txn.coa ?? null);
  const isParentTransfer = Boolean(defaultCoa && chartOfAccountsUtils.isTransferCOA(defaultCoa));
  const overflowDefaultCoa = isParentTransfer ? UNCATEGORIZED_COA : defaultCoa;

  splitItems.push({
    coa: defaultCoa,
    tags: txn.tags || undefined,
    businessId: txn.businessId || undefined,
    projectId: txn.projectId || undefined,
    customerId: txn.customerId || undefined,
    isBillable: txn.isBillable ?? undefined,
    amount: 0,
  });

  splitItems.push({
    // Default additional lines to "Unspecified" (blank category).
    coa: null,
    businessId: txn.businessId || undefined,
    amount: 0,
  });

  splitItems.push({
    // Transfer parents default overflow to "Uncategorized"; others default to "Unspecified".
    coa: overflowDefaultCoa,
    businessId: txn.businessId || undefined,
    amount: 0,
    isOverflow: true,
  });

  return mkSplit({ items: splitItems });
};

/**
 * Normalizes a series split for editing:
 * - Ensures the overflow line exists and is marked (`isOverflow` + memo marker).
 * - Ensures the overflow line is last.
 * - Upgrades legacy overflow representations (e.g. memo-only or 0-amount-last-row).
 */
export const normalizeSeriesSplitForEdit = (split: RecordOf<SplitProps>): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);
  if (items.size === 0) {
    return split;
  }

  if (items.some((item) => isOverflowSplitItem(item))) {
    // Ensure any legacy overflow marker is upgraded to `isOverflow: true` for persistence
    // without overwriting user-entered notes on already-marked overflow rows.
    const upgraded = split.set(
      'items',
      items.map((item) =>
        isOverflowSplitItem(item)
          ? item.isOverflow
            ? item.set('isOverflow', true)
            : item.set('isOverflow', true).set('memo', item.memo || '')
          : item,
      ),
    ) as RecordOf<SplitProps>;
    return ensureOverflowSplitIsLast(upgraded);
  }

  const reverseIndex = items.reverse().findIndex((item) => item.amount === 0);
  if (reverseIndex === -1) {
    return split;
  }
  const overflowIndex = items.size - 1 - reverseIndex;
  // Legacy: a 0-amount row may have been treated as the overflow placeholder without markers. Ensure memo marker too.
  return ensureOverflowSplitIsLast(split.setIn(['items', overflowIndex, 'isOverflow'], true));
};

/**
 * Ensures a split has an overflow placeholder line.
 *
 * If an empty row exists (amount=0, coa=null), it will be re-used as the overflow line by setting markers.
 * Otherwise a new overflow row is appended. Always keeps overflow row last.
 */
export const createOverflowSplitLine = (split: RecordOf<SplitProps>): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);

  if (items.some((item) => isOverflowSplitItem(item))) {
    return ensureOverflowSplitIsLast(split);
  }

  const reverseIndex = items
    .reverse()
    .findIndex((item) => item.amount === 0 && item.coa === null && !isOverflowSplitItem(item));
  const emptyIndex = reverseIndex === -1 ? -1 : items.size - 1 - reverseIndex;

  if (emptyIndex >= 0) {
    // Ensure overflow markers are discoverable even if `isOverflow` isn't persisted: always set memo marker too.
    return ensureOverflowSplitIsLast(split.setIn(['items', emptyIndex, 'isOverflow'], true));
  }

  return ensureOverflowSplitIsLast(
    split.set(
      'items',
      items.push(
        mkSplitItem({
          amount: 0,
          coa: null,
          isOverflow: true,
        }),
      ),
    ),
  );
};

/**
 * Ensures a minimum number of editable split lines.
 * - Always ensures at least 2 **non-overflow** lines.
 * - In overflow mode, also ensures an overflow line exists and is last.
 */
export const ensureMinSplitLines = (split: RecordOf<SplitProps>, isOverflowMode: boolean): RecordOf<SplitProps> => {
  const toItemsList = (s: RecordOf<SplitProps>) =>
    (Array.isArray(s.items) ? ImmutableList(mkSplitItems(s.items)) : s.items) ??
    ImmutableList<RecordOf<SplitItemProps>>();

  let nextSplit = split;

  if (isOverflowMode) {
    // Ensure we always have an overflow line (and that it stays last).
    nextSplit = ensureOverflowSplitIsLast(createOverflowSplitLine(nextSplit));
  }

  let items = toItemsList(nextSplit);
  const nonOverflowCount = items.filter((item) => !isOverflowSplitItem(item)).size;
  const minNonOverflow = 2;

  if (nonOverflowCount >= minNonOverflow) {
    return nextSplit;
  }

  const toAdd = minNonOverflow - nonOverflowCount;
  const overflowIndex = items.findIndex((item) => isOverflowSplitItem(item));

  for (let i = 0; i < toAdd; i += 1) {
    const newItem = mkSplitItem({ amount: 0, coa: null });
    items = overflowIndex >= 0 ? items.insert(overflowIndex, newItem) : items.push(newItem);
  }

  return nextSplit.set('items', items);
};

/**
 * Builds the split state used by the SplitDialog (web/mobile):
 * - If `split` is null: create an initial split state (transaction or series).
 * - If `split` exists: optionally normalize for overflow mode and ensure minimum lines.
 */
export const getInitialSplitState = (args: {
  txn: TransactionProps;
  split: RecordOf<SplitProps> | null;
  isOverflowMode: boolean;
}): RecordOf<SplitProps> => {
  const { txn, split, isOverflowMode } = args;

  const splitItemCount = getLength(split?.items);

  if (split == null || splitItemCount === 0) {
    const init = isOverflowMode ? createSeriesInitState(txn) : createInitState(txn);
    return ensureMinSplitLines(init, isOverflowMode);
  }

  const normalized = isOverflowMode
    ? normalizeLegacyNACoasForSeriesSplitRecord(normalizeSeriesSplitForEdit(split))
    : split;
  const withMinLines = ensureMinSplitLines(normalized, isOverflowMode);

  // Overflow line defaults:
  // - Match first non-overflow COA, unless that COA is a transfer, then use Uncategorized.
  // - Match first non-overflow businessId, normalizing personal sentinel values to unset.
  // - Never override a user-set non-transfer COA, but do normalize legacy/invalid transfer COAs on overflow.
  if (isOverflowMode) {
    const items = splitItemsToRecordList(withMinLines);
    const overflowIndex = items.findIndex((item) => isOverflowSplitItem(item));
    const firstNonOverflowIndex = items.findIndex((item) => !isOverflowSplitItem(item));
    if (overflowIndex >= 0 && firstNonOverflowIndex >= 0) {
      const overflowItem = items.get(overflowIndex);
      const firstNonOverflowItem = items.get(firstNonOverflowIndex);
      const overflowCoa = overflowItem?.coa ?? null;
      const firstCoa = firstNonOverflowItem?.coa ?? null;
      const desiredOverflowBusinessId = accountsUtils.normalizeBusinessIdForUsage(firstNonOverflowItem?.businessId);
      const desiredOverflowCoa =
        firstCoa && chartOfAccountsUtils.isTransferCOA(firstCoa) ? UNCATEGORIZED_COA : firstCoa;
      const hasExplicitPersonalUsage = overflowItem?.businessId === '0' || overflowItem?.businessId === 'PERSONAL';
      const hasBusinessContext = Boolean(
        hasExplicitPersonalUsage ||
          accountsUtils.normalizeBusinessIdForUsage(overflowItem?.businessId) ||
          overflowItem?.customerId ||
          overflowItem?.projectId,
      );
      const isUncategorizedPlaceholder =
        Boolean(overflowItem && isOverflowSplitItem(overflowItem)) &&
        !hasBusinessContext &&
        chartOfAccountsUtils.coasAreEqual(overflowCoa, UNCATEGORIZED_COA);
      const shouldDefaultOverflowValues =
        overflowCoa == null || Boolean(chartOfAccountsUtils.isTransferCOA(overflowCoa)) || isUncategorizedPlaceholder;

      if (shouldDefaultOverflowValues) {
        return withMinLines
          .setIn(['items', overflowIndex, 'coa'], desiredOverflowCoa)
          .setIn(['items', overflowIndex, 'businessId'], desiredOverflowBusinessId);
      }

      if (
        desiredOverflowBusinessId &&
        !hasBusinessContext &&
        chartOfAccountsUtils.coasAreEqual(overflowCoa, desiredOverflowCoa)
      ) {
        return withMinLines.setIn(['items', overflowIndex, 'businessId'], desiredOverflowBusinessId);
      }
    }
  }

  return withMinLines;
};

/**
 * Handles "Add Left To Split" behavior:
 * - If there is exactly one empty non-overflow line (amount=0), fill it with `leftToSplit`.
 * - Otherwise, insert a new split line (before the overflow row if present).
 */
export const createLeftToSplitLine = (split: RecordOf<SplitProps>, leftToSplit: number): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);

  // In series mode we have an "Overflow" split line which is always amount=0; never treat that as an empty field.
  const emptyFields = items?.filter((item) => item.amount === 0 && !isOverflowSplitItem(item));
  let newSplit: RecordOf<SplitProps>;
  if (emptyFields?.size === 1) {
    // fill in the empty split item
    const emptyIndex = items?.findIndex(
      (item: SplitItemProps) => item === (Array.isArray(emptyFields) ? emptyFields[0] : emptyFields?.first()),
    );
    newSplit = split.setIn(['items', emptyIndex, 'amount'], leftToSplit);
  } else {
    // create a new split item
    const newItem = mkSplitItem({
      amount: leftToSplit,
      coa: null,
    });
    const overflowIndex = items?.findIndex((item) => isOverflowSplitItem(item)) ?? -1;
    const nextItems =
      overflowIndex >= 0 && items
        ? items.insert(overflowIndex, newItem) // keep overflow row last
        : items?.push(newItem);
    newSplit = split.set('items', nextItems);
  }
  return newSplit;
};

/**
 * Adds a new empty split line (amount=0, coa=null).
 * In overflow mode, inserts it before the overflow placeholder line and keeps overflow last.
 */
export const addEmptySplitLine = (split: RecordOf<SplitProps>, isOverflowMode: boolean): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);
  const newItem = mkSplitItem({ amount: 0, coa: null });

  if (!isOverflowMode) {
    return split.set('items', items.push(newItem));
  }

  const overflowIndex = items.findIndex((item) => isOverflowSplitItem(item));
  const nextItems = overflowIndex >= 0 ? items.insert(overflowIndex, newItem) : items.push(newItem);
  return ensureOverflowSplitIsLast(split.set('items', nextItems));
};

/**
 * Evenly distributes a currency amount into `count` whole-cent shares.
 * The leading `|remainder|` shares absorb the extra signed cent so the shares always sum to
 * the input rounded to cents.
 *
 * Integer cent math only: `amount * 100` can land a hair below the true integer for negative
 * amounts (e.g. -327.66 * 100 === -32766.000000000004), where flooring the per-share cents
 * drops a whole extra cent per share and the modulo remainder rounds to -0, so nothing
 * compensates. Round to cents first, then split with truncation plus a signed cent remainder.
 */
export const distributeAmountEvenly = (amount: number, count: number): number[] => {
  const totalCents = Math.round(amount * 100);
  const baseCents = Math.trunc(totalCents / count);
  const remainderCents = totalCents - baseCents * count; // signed, |remainderCents| < count
  const remainderCount = Math.abs(remainderCents);
  const remainderUnit = remainderCents === 0 ? 0 : remainderCents > 0 ? 1 : -1;
  return Array.from({ length: count }, (_, i) => (baseCents + (i < remainderCount ? remainderUnit : 0)) / 100);
};

/**
 * Splits `leftToSplitNum` evenly across the non-overflow split lines, keeping cents balanced via remainder logic.
 * The overflow placeholder line (if present) is never allocated to and is forced to amount=0.
 */
export const splitEvenlyUtil = (split: RecordOf<SplitProps>, leftToSplitNum: number): RecordOf<SplitProps> => {
  const items = splitItemsToRecordList(split);

  // "Divide among Splits" should never allocate to the series-only Overflow row.
  // That row is a special UI target and must remain amount=0 (the UI hides its amount).
  const nonOverflowIndices = items
    .map((item, idx) => (isOverflowSplitItem(item) ? null : idx))
    .filter((idx): idx is number => idx !== null);

  const numSplits = nonOverflowIndices.size;
  if (numSplits === 0) {
    return split;
  }

  const shares = distributeAmountEvenly(leftToSplitNum, numSplits);

  let newSplits = split;

  // Defensive: keep any overflow rows at amount=0.
  items.forEach((item, idx) => {
    if (isOverflowSplitItem(item) && (item.amount ?? 0) !== 0) {
      newSplits = newSplits.setIn(['items', idx, 'amount'], 0);
    }
  });

  for (let i = 0; i < numSplits; i++) {
    const idx = nonOverflowIndices.get(i) ?? i;
    const amt = items?.get(idx)?.amount ?? 0;
    newSplits = newSplits.setIn(['items', idx, 'amount'], Math.round((amt + (shares[i] ?? 0)) * 100) / 100);
  }

  return newSplits;
};

/** Returns index of the first non-overflow split line, or -1 if none. */
export const getFirstNonOverflowSplitIndex = (items: SplitItemProps[]): number =>
  items.findIndex((item) => !isOverflowSplitItem(item));

/**
 * SplitDialog insertions always happen before the overflow placeholder or at the end of the list.
 *
 * We intentionally avoid diffing item object identity here because `normalizeSplits()` clones items,
 * so the "same" row will not be referentially equal across renders.
 */
export const getInsertedEditableSplitIndex = (
  currentItems: SplitItemProps[],
  nextItems: SplitItemProps[],
): number | null => {
  if (nextItems.length <= currentItems.length) return null;

  const overflowIndex = currentItems.findIndex((item) => isOverflowSplitItem(item));
  return overflowIndex >= 0 ? overflowIndex : currentItems.length;
};

/**
 * Returns the first non-overflow split item if one exists.
 * Falls back to the first item when all items are overflow (defensive).
 */
export const getFirstNonOverflowSplitItem = (items: SplitItemProps[]): SplitItemProps | null => {
  const idx = getFirstNonOverflowSplitIndex(items);
  return idx >= 0 ? (items[idx] ?? null) : (items[0] ?? null);
};

/**
 * Validates SplitDialog item state and returns a compact summary of errors:
 * - **splitRowError**: missing category OR zero-amount non-overflow line exists
 * - **oneSplit**: only a single editable split line remains (must be 0 or >=2)
 * - **leftToSplitInvalid**: txn has non-zero amount but splits don't balance (with mode-specific allowances)
 */
export const validateSplitDialogState = (args: {
  items: SplitItemProps[];
  isTxnNot0Amount: boolean;
  leftToSplit: number;
  mode: SplitDialogModeV2;
  isOverflowMode: boolean;
}): { hasError: boolean; splitRowError: boolean; oneSplit: boolean; leftToSplitInvalid: boolean } => {
  const { items, isTxnNot0Amount, leftToSplit, mode } = args;

  const splitRowError = items.some(
    (splitItem) => splitItem.coa === null || (splitItem.amount === 0 && !isOverflowSplitItem(splitItem)),
  );
  const editableItemCount =
    mode === 'series' ? items.filter((splitItem) => !isOverflowSplitItem(splitItem)).length : items.length;
  const oneSplit = editableItemCount === 1; // need to be either 0 or > 1
  const leftToSplitInvalid = isTxnNot0Amount && leftToSplit !== 0;

  return {
    splitRowError,
    oneSplit,
    leftToSplitInvalid,
    hasError: (splitRowError || oneSplit || leftToSplitInvalid) && items.length !== 0,
  };
};

/**
 * Applies persistence rules before saving split edits:
 * - In overflow mode: forces overflow markers (`isOverflow`) onto overflow item(s).
 * - Optionally inherits the parent txn's COA onto the first editable (non-overflow) line when it is missing.
 */
export const createSplitToPersist = (args: {
  split: RecordOf<SplitProps>;
  items: SplitItemProps[];
  txnCoa: TransactionProps['coa'];
  mode: SplitDialogModeV2;
  isOverflowMode: boolean;
}): RecordOf<SplitProps> => {
  const { split, items, txnCoa, mode, isOverflowMode } = args;

  const firstNonOverflowIndex = getFirstNonOverflowSplitIndex(items);
  const shouldInheritParentCoa =
    (mode === 'transaction' || mode === 'reminder' || mode === 'series') &&
    firstNonOverflowIndex >= 0 &&
    Boolean(txnCoa) &&
    !items[firstNonOverflowIndex]?.coa;

  const itemsWithInheritedCoa =
    items?.map((item, idx) =>
      shouldInheritParentCoa && idx === firstNonOverflowIndex ? { ...item, coa: txnCoa ?? null } : item,
    ) ?? [];

  const itemsWithDefaultExclusions = itemsWithInheritedCoa.map((item) => applyDefaultSplitItemExclusions(item));
  const itemsForPersist = isOverflowMode
    ? itemsWithDefaultExclusions.map((item) => (isOverflowSplitItem(item) ? { ...item, isOverflow: true } : item))
    : itemsWithDefaultExclusions;
  const normalizedItemsForPersist = isOverflowMode
    ? normalizeSeriesSplitItemsForPersistence(itemsForPersist)
    : itemsForPersist;

  // Persist as Immutable split-item records to keep downstream code consistent.
  return split.set('items', ImmutableList(mkSplitItems(normalizedItemsForPersist))) as RecordOf<SplitProps>;
};
